import React from 'react'
import moment from 'moment'
import cn from 'classnames'

import { PATHS } from 'Config/Routing/paths'
import {
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_INTERNAL_STATUSES,
} from '../../shared/constants'
import {
  addAuthParamsToCampaignBuilderUrl,
  getCampaignBuilderPageUrl,
} from '../Country/Page/Pipeline/PipelineView/utils'
import { getLastStepReached } from '../../Components/ClientDashboard/Components/CampaignCard/utils'
import StatusLabelSelfServe from '../../Components/StatusLabelSelfServe'
import { GenericButton } from '../../Components/Buttons/GenericButton'
import classNames from 'classnames'

const isDeclinedCampaign = ({ internalStatus, paymentStatus }) =>
  internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT &&
  (paymentStatus === 'refunded' || paymentStatus === 'canceled')

const getCampaignPriority = ({ internalStatus, reviewMeetingDate }) => {
  const shouldDisplayReviewBooked =
    reviewMeetingDate &&
    moment(reviewMeetingDate).isAfter(moment()) &&
    (internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ||
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW)

  if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.REJECTED) {
    return 7
  } else if (shouldDisplayReviewBooked) {
    // This is to show campaigns with a coming review meeting on top.
    // After the date has passed, the campaign will be shown in its priority based on the internal status.
    return 6
  } else if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW) {
    return 5
  } else if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE) {
    return 4
  } else if (
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.PAYMENT
  ) {
    return 3
  } else if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT) {
    return 2
  } else if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.COMPLETED) {
    return 1
  } else {
    return 0
  }
}

const getRecentCampaigns = campaigns => {
  const campaignsWithoutInserts = filterOutInsertsCampaigns(campaigns)

  const sortedCampaignsByPriority = campaignsWithoutInserts.sort((a, b) => {
    const priorityA = getCampaignPriority(a)
    const priorityB = getCampaignPriority(b)

    if (priorityA !== priorityB) {
      return priorityB - priorityA
    } else {
      if (a.createdAt < b.createdAt) {
        return 1
      } else if (a.createdAt > b.createdAt) {
        return -1
      } else {
        return 0
      }
    }
  })
  return sortedCampaignsByPriority.slice(0, 6)
}

const getTextRecentCampaignCard = ({ internalStatus, startDate }) => {
  switch (internalStatus) {
    case CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW: {
      return 'This takes no more than a few business hours! If we identify any problem, we will be in touch.'
    }
    case CAMPAIGN_INTERNAL_STATUSES.ACTIVE: {
      return 'Campaign in progress'
    }
    case CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW:
    case CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF:
    case CAMPAIGN_INTERNAL_STATUSES.PAYMENT: {
      const specificDate = moment.utc(+startDate).startOf('day')
      const currentDate = moment.utc().startOf('day')
      const differenceInDays = specificDate.diff(currentDate, 'days')
      if (differenceInDays > 0) {
        return `${differenceInDays} days left before launch`
      } else {
        return ''
      }
    }
    default: {
      return ''
    }
  }
}

const filterOutInsertsCampaigns = campaigns =>
  campaigns.filter(campaign => campaign.type !== CAMPAIGN_ENUM_TYPES.INSERTS)

const getRecentCampaignsCards = ({
  recentCampaigns,
  clientId,
  classes,
  history,
  smallScreen,
}) =>
  recentCampaigns.map(
    ({
      campaignId,
      campaignName,
      internalStatus,
      startDate,
      lastActiveStep,
      type,
      subtype,
      reviewMeetingDate,
    }) => {
      const text = getTextRecentCampaignCard({ internalStatus, startDate })
      // const isDeclined = isDeclinedCampaign({ internalStatus, paymentStatus })
      const isDeclined = internalStatus === CAMPAIGN_INTERNAL_STATUSES.REJECTED

      const showButton =
        internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT || isDeclined

      if (showButton) {
        const buttonText = isDeclined ? 'Review' : 'Resume'
        const campaignBuilderPageUrl = getCampaignBuilderPageUrl({
          campaignId,
          clientId,
          lastActiveStep: getLastStepReached(lastActiveStep, type),
          channel: type,
          subtype,
        })
        const resumeUrl = addAuthParamsToCampaignBuilderUrl(
          campaignBuilderPageUrl,
          clientId,
        )

        const href = !isDeclined
          ? resumeUrl
          : `${history.location.pathname}${
              PATHS.dashboard.rejected
            }/${campaignId}`
        return (
          <div
            key={campaignId}
            className={cn(
              'Home-recentCampaignCard',
              classes.recentCampaignCard,
              classes.recentCampaignCardAction,
            )}
            cypress_id={'recentCampaignCard'}
          >
            <div className={classes.cardTitleAndStatusContainer}>
              <div
                className={classes.cardTitle}
                cypress_id={'recentCampaignCardTitle'}
              >
                {campaignName}
              </div>
              <div className={classes.marginTop10}>
                <StatusLabelSelfServe
                  status={internalStatus}
                  isDeclined={isDeclined}
                  reviewMeetingDate={reviewMeetingDate}
                  cypress_id={'recentCampaignStatus'}
                />
              </div>
            </div>
            <div
              className={classNames(
                classes.recentCampaignTextWrapBlock,
                classes.recentCampaignCTAWrapper,
              )}
            >
              <GenericButton
                label={buttonText}
                href={href}
                variant="primary"
                customStyles={{
                  width: smallScreen ? '100%' : '194px',
                  height: smallScreen ? '40px' : '48px',
                  alignSelf: 'center',
                  margin: 'auto 0',
                }}
                cypress_id={'actionBtnInCampaignCard'}
              />
            </div>
          </div>
        )
      } else {
        const isInReview =
          internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW
        let onClick

        if (isInReview) {
          const campaignBuilderPageUrl = getCampaignBuilderPageUrl({
            campaignId,
            clientId,
            lastActiveStep: getLastStepReached(lastActiveStep, type),
            channel: type,
            subtype,
          })
          const resumeUrl = addAuthParamsToCampaignBuilderUrl(
            campaignBuilderPageUrl,
            clientId,
          )
          onClick = () => {
            window.location.href = resumeUrl
          }
        }

        return (
          <div
            key={campaignId}
            className={classNames(
              classes.recentCampaignCard,
              'Home-recentCampaignCard',
            )}
            cypress_id={'recentCampaignCard'}
            onClick={onClick}
            style={isInReview ? { cursor: 'pointer' } : {}}
          >
            <div
              className={classes.cardTitle}
              cypress_id={'recentCampaignCardTitle'}
            >
              {campaignName}
            </div>

            <div className={classes.recentCampaignTextWrapBlock}>
              <div className={classes.recentCampaignTextBlock}>
                <div className={classes.statusWrapper}>
                  <StatusLabelSelfServe
                    status={internalStatus}
                    isDeclined={isDeclined}
                    reviewMeetingDate={reviewMeetingDate}
                    cypress_id={'recentCampaignStatus'}
                  />
                </div>
                <div cypress_id={'textRecentCampaignCard'}>{text}</div>
              </div>
            </div>
          </div>
        )
      }
    },
  )

const HtmlToReact = html => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const reactNodes = []

  const parseNode = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent
      const urlRegex = /(https?:\/\/[^\s]+)/g
      const parts = text.split(urlRegex)

      if (parts.length === 1) {
        // No URLs found, return the text node
        return text
      }

      const elements = parts.reduce((acc, part, index) => {
        if (part.match(urlRegex)) {
          // Found a URL, wrap it in <a> tag
          const url = part
          const key = `${text}-${index}`
          acc.push(
            React.createElement('a', { href: url, key, target: '_blank' }, url),
          )
        } else if (part) {
          // Found a non-URL part, add it as a text node
          acc.push(part)
        }
        return acc
      }, [])

      return elements
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const children = Array.from(node.childNodes).map(parseNode)
      const props = Array.from(node.attributes).reduce((result, attr) => {
        result[attr.name] = attr.value
        return result
      }, {})

      return React.createElement(node.tagName.toLowerCase(), props, children)
    }
  }

  Array.from(doc.body.childNodes).forEach(node => {
    const reactNode = parseNode(node)
    if (reactNode) {
      reactNodes.push(reactNode)
    }
  })

  return reactNodes
}

const getDeclinedCampaignCount = campaigns =>
  campaigns.reduce((count, campaign) => {
    if (campaign.internalStatus === CAMPAIGN_INTERNAL_STATUSES.REJECTED) {
      count++
    }
    return count
  }, 0)

const getHasSubmittedCampaigns = campaigns =>
  campaigns.filter(
    camp => camp.internalStatus != CAMPAIGN_INTERNAL_STATUSES.DRAFT,
  ).length > 0

export {
  getRecentCampaigns,
  getTextRecentCampaignCard,
  isDeclinedCampaign,
  getRecentCampaignsCards,
  HtmlToReact,
  getDeclinedCampaignCount,
  getHasSubmittedCampaigns,
}
